import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { Animated } from 'react-native'
import Snackbar from '@components/Snackbar'

export enum SnackbarThemes {
  normal,
  error,
  warning,
  success,
}

export interface SnackbarMessage {
  text: string,
  theme?: SnackbarThemes
}

interface SnackbarProviderProps {
  children: JSX.Element | JSX.Element[]
}

interface DefaultValues {
  setSnackbarMessage: (message: string, theme?: SnackbarThemes) => void
}

const defaultValue: DefaultValues = {
  setSnackbarMessage: () => {},
}

// Times in milliseconds
const ANIMATION_DURATION = 200
const DISMISS_TIMEOUT = 2500

/**
 * Context & Hook
 */
const SnackbarContext = createContext(defaultValue)
export const useSnackbar = () => {
  return useContext(SnackbarContext)
}

/**
 * Provider component for App.tsx
 */

export const SnackbarProvider = (props: SnackbarProviderProps) => {
  const { children } = props
  const [message, setMessage] = useState<SnackbarMessage | undefined>(undefined)
  const animationValue = useRef(new Animated.Value(0))
  const timeoutRef = useRef<Maybe<ReturnType<typeof setTimeout>>>(undefined)

  const setSnackbarMessage = (text: string, theme?: SnackbarThemes) => {
    if (!message) {
      setMessage({ text, theme })
      slideIn()
      return
    }
    replaceMessage({ text, theme })
  }

  // Animations

  const replaceMessage = (newMessage: SnackbarMessage) => {
    Animated.timing(animationValue.current, {
      toValue: 0,
      duration: ANIMATION_DURATION, // ms
      useNativeDriver: false,
    }).start(
      () => {
        setMessage(newMessage)
        slideIn()
      },
    )
  }

  const slideIn = () => {
    Animated.timing(animationValue.current, {
      toValue: 1,
      duration: ANIMATION_DURATION, // ms
      useNativeDriver: false,
    }).start()
  }

  const slideOut = () => {
    Animated.timing(animationValue.current, {
      toValue: 0,
      duration: ANIMATION_DURATION, // ms
      useNativeDriver: false,
    }).start(() => setMessage(undefined))
  }

  // Manage snackbar auto-dismiss timeout
  useEffect(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current)

    timeoutRef.current = setTimeout(() => {
      slideOut()
      if (!timeoutRef.current) return
      clearTimeout(timeoutRef.current)
    }, DISMISS_TIMEOUT)
  }, [message])

  return (
    <SnackbarContext.Provider value={{ setSnackbarMessage }}>
      { children }
      { message !== undefined &&
        <Snackbar
          animationValue={ animationValue.current }
          message={ message }
          dismissSnackbar={ slideOut }
        />
      }
    </SnackbarContext.Provider>
  )
}
